import { Company } from '@app/modules/company/models/company.model';
import { SupportedCountry } from '@app/modules/supported-country/models/supported-country.model';
import {
  OfficerCompanyType,
  OfficerType,
  OfficerTypeEnum,
} from '@generated/graphql';
import { ExistsOfficerType } from '@shared/component/job/component/company-incorporation/dialog/add-officer-dialog/classes/exists-officer.type';
import { OfficerHelper } from '@shared/helpers/officer.helper';
import { sortBy, uniqBy } from 'lodash-es';

export class ExistingOfficerState {
  public targetRole: 'Director' | 'Shareholder';

  public existingOfficers: ExistsOfficerType[] = [];

  public sourceOfficers: OfficerType[] = [];

  public excludedOfficerIds: string[] = [];

  public officerHelper = new OfficerHelper();

  public countryOfIncorporation: SupportedCountry;

  public selectedOfficerId: string;

  constructor() {
    //
  }

  public appendOfficersFromServer(officers: OfficerType[]): this {
    if (officers.length) {
      this.sourceOfficers = uniqBy(
        [...this.sourceOfficers, ...officers],
        (officer) => officer.id,
      );

      const normalOfficers = officers.map((o) => ({
        id: o.id,
        name: this.officerHelper.getOfficerName(o.officerProfile),
        email: this.officerHelper.getOfficerEmail(o.officerProfile),
        type:
          o.officerProfile.__typename === 'OfficerCompanyType'
            ? OfficerTypeEnum.Corporate
            : OfficerTypeEnum.Individual,
        refId: o.id,
      }));

      const companyOfficers = officers.filter(
        (o) => o.officerProfile.__typename === 'OfficerCompanyType',
      );

      const representatives = companyOfficers.map((o) => {
        const representative = (o.officerProfile as OfficerCompanyType)
          .representative;

        return {
          id: representative.id,
          name: this.officerHelper.getOfficerName(
            representative.officerProfile,
          ),
          email: this.officerHelper.getOfficerEmail(
            representative.officerProfile,
          ),
          type: OfficerTypeEnum.Individual,
          refId: representative.id,
        };
      });

      this.existingOfficers = [
        ...this.existingOfficers,
        ...normalOfficers,
        ...representatives,
      ];

      this.normalizeExistingOfficers();
    }

    return this;
  }

  public setExcludedOfficerIds(company: Company): this {
    if (this.targetRole === 'Director') {
      this.excludedOfficerIds = [...company.directors.map((s) => s.officer.id)];
    } else if (this.targetRole === 'Shareholder') {
      this.excludedOfficerIds = [
        ...company.shareholders.map((d) => d.officer.id),
      ];
    }

    return this;
  }

  public findSelectedOfficer(existingOfficer: ExistsOfficerType) {
    let foundOfficer = this.sourceOfficers.find(
      (o) => o.id === existingOfficer.id,
    );

    if (!foundOfficer) {
      const companyOfficers = this.sourceOfficers.filter(
        (o) => o.officerProfile.__typename === 'OfficerCompanyType',
      );

      const foundOfficerCompany = companyOfficers.find(
        (o) =>
          (o.officerProfile as OfficerCompanyType).representative.id ===
          existingOfficer.id,
      );

      foundOfficer = (foundOfficerCompany.officerProfile as OfficerCompanyType)
        .representative;
    }

    return foundOfficer;
  }

  public normalizeDirectorExistingOfficers(): this {
    if (this.countryOfIncorporation.country.code.toUpperCase() === 'SG') {
      this.existingOfficers = this.existingOfficers.filter(
        (e) => e.type !== OfficerTypeEnum.Corporate,
      );
    }

    return this.normalizeExistingOfficers();
  }

  public normalizeExistingOfficers(): this {
    this.existingOfficers = sortBy(
      uniqBy(this.existingOfficers, (o) => o.id),
      (o) => o.name,
    );

    return this;
  }

  public reset() {
    this.selectedOfficerId = undefined;
  }
}
